import parsePhoneNumber, {
  validatePhoneNumberLength,
} from 'libphonenumber-js/max';

export const PHONE_VALIDATION_ERRORS = {
  MISSING_DIAL_CODE: 'missing_dial_code',
  INVALID_PHONE: 'invalid_phone',
  INVALID_BRAZIL_AREA_CODE: 'invalid_brazil_area_code',
  PHONE_TOO_SHORT: 'phone_too_short',
  PHONE_TOO_LONG: 'phone_too_long',
  PHONE_INVALID_LENGTH: 'phone_invalid_length',
};

export const VALID_BRAZILIAN_AREA_CODES = new Set([
  '11',
  '12',
  '13',
  '14',
  '15',
  '16',
  '17',
  '18',
  '19',
  '21',
  '22',
  '24',
  '27',
  '28',
  '31',
  '32',
  '33',
  '34',
  '35',
  '37',
  '38',
  '41',
  '42',
  '43',
  '44',
  '45',
  '46',
  '47',
  '48',
  '49',
  '51',
  '53',
  '54',
  '55',
  '61',
  '62',
  '63',
  '64',
  '65',
  '66',
  '67',
  '68',
  '69',
  '71',
  '73',
  '74',
  '75',
  '77',
  '79',
  '81',
  '82',
  '83',
  '84',
  '85',
  '86',
  '87',
  '88',
  '89',
  '91',
  '92',
  '93',
  '94',
  '95',
  '96',
  '97',
  '98',
  '99',
]);

export const isPhoneInputAcceptable = value => {
  const normalized = String(value || '').trim();
  if (!normalized) return true;

  if (!/^[+\d\s().-]+$/.test(normalized)) return false;
  if ((normalized.match(/\+/g) || []).length > 1) return false;
  if (normalized.includes('+') && !normalized.startsWith('+')) return false;

  return true;
};

export const buildPhoneCandidatesForInput = (value, activeDialCode = '') => {
  const input = String(value || '').trim();
  if (!input || !isPhoneInputAcceptable(input)) return [];

  const compactValue = input.replace(/[^\d+]/g, '');
  const digitsOnly = input.replace(/\D/g, '');
  const normalizedDialCode = String(activeDialCode || '').trim();

  return [
    ...new Set(
      [
        input,
        compactValue,
        compactValue.startsWith('00')
          ? `+${compactValue.replace(/^00+/, '')}`
          : '',
        !input.startsWith('+') && digitsOnly && normalizedDialCode
          ? `${normalizedDialCode}${digitsOnly}`
          : '',
        !input.startsWith('+') && digitsOnly ? `+${digitsOnly}` : '',
      ].filter(Boolean)
    ),
  ];
};

export const getParsedPhoneForInput = (value, activeDialCode = '') => {
  const candidates = buildPhoneCandidatesForInput(value, activeDialCode);

  for (const candidate of candidates) {
    const parsed = parsePhoneNumber(candidate);
    if (parsed?.isValid?.()) {
      return parsed;
    }
  }

  return null;
};

export const getBrazilianAreaCode = parsedPhoneNumber => {
  if (!parsedPhoneNumber || parsedPhoneNumber.country !== 'BR') {
    return '';
  }

  const nationalDigits = `${parsedPhoneNumber.nationalNumber || ''}`.replace(
    /\D/g,
    ''
  );

  return nationalDigits.slice(0, 2);
};

export const isBrazilianAreaCodeValid = areaCode =>
  VALID_BRAZILIAN_AREA_CODES.has(String(areaCode || ''));

export const validateBrazilianPhone = parsedPhoneNumber => {
  if (!parsedPhoneNumber || parsedPhoneNumber.country !== 'BR') {
    return {
      isValid: true,
      errorKey: '',
    };
  }

  const nationalDigits = `${parsedPhoneNumber.nationalNumber || ''}`.replace(
    /\D/g,
    ''
  );

  if (nationalDigits.length < 10 || nationalDigits.length > 11) {
    return {
      isValid: false,
      errorKey: PHONE_VALIDATION_ERRORS.INVALID_BRAZIL_AREA_CODE,
    };
  }

  const areaCode = getBrazilianAreaCode(parsedPhoneNumber);
  if (!isBrazilianAreaCodeValid(areaCode)) {
    return {
      isValid: false,
      errorKey: PHONE_VALIDATION_ERRORS.INVALID_BRAZIL_AREA_CODE,
    };
  }

  return {
    isValid: true,
    errorKey: '',
  };
};

export const inferBrazilianPhoneInputError = (value, activeDialCode = '') => {
  if (String(activeDialCode || '').trim() !== '+55') {
    return '';
  }

  const digitsOnly = String(value || '').replace(/\D/g, '');
  if (!digitsOnly) {
    return '';
  }

  const nationalDigits =
    digitsOnly.startsWith('55') && [12, 13].includes(digitsOnly.length)
      ? digitsOnly.slice(2)
      : digitsOnly;

  if (nationalDigits.length < 10 || nationalDigits.length > 11) {
    return PHONE_VALIDATION_ERRORS.INVALID_BRAZIL_AREA_CODE;
  }

  const areaCode = nationalDigits.slice(0, 2);
  if (!isBrazilianAreaCodeValid(areaCode)) {
    return PHONE_VALIDATION_ERRORS.INVALID_BRAZIL_AREA_CODE;
  }

  return '';
};

export const inferGenericPhoneInputError = (value, activeDialCode = '') => {
  const candidates = buildPhoneCandidatesForInput(value, activeDialCode);

  const prioritizedCandidates = [
    ...candidates.filter(candidate => candidate.startsWith(activeDialCode)),
    ...candidates.filter(candidate => !candidate.startsWith(activeDialCode)),
  ];

  for (const candidate of prioritizedCandidates) {
    const lengthResult = validatePhoneNumberLength(candidate);

    if (lengthResult === 'TOO_SHORT') {
      return PHONE_VALIDATION_ERRORS.PHONE_TOO_SHORT;
    }

    if (lengthResult === 'TOO_LONG') {
      return PHONE_VALIDATION_ERRORS.PHONE_TOO_LONG;
    }

    if (lengthResult === 'INVALID_LENGTH') {
      return PHONE_VALIDATION_ERRORS.PHONE_INVALID_LENGTH;
    }
  }

  return '';
};

export const validatePhoneForInput = (value, activeDialCode = '') => {
  if (!String(value || '').trim()) {
    return {
      isValid: true,
      normalizedNumber: '',
      parsedPhone: null,
      errorKey: '',
    };
  }

  if (!activeDialCode) {
    return {
      isValid: false,
      normalizedNumber: '',
      parsedPhone: null,
      errorKey: PHONE_VALIDATION_ERRORS.MISSING_DIAL_CODE,
    };
  }

  const parsedPhone = getParsedPhoneForInput(value, activeDialCode);
  if (!parsedPhone) {
    const brazilianInputError = inferBrazilianPhoneInputError(
      value,
      activeDialCode
    );
    if (brazilianInputError) {
      return {
        isValid: false,
        normalizedNumber: '',
        parsedPhone: null,
        errorKey: brazilianInputError,
      };
    }

    const genericInputError = inferGenericPhoneInputError(
      value,
      activeDialCode
    );
    if (genericInputError) {
      return {
        isValid: false,
        normalizedNumber: '',
        parsedPhone: null,
        errorKey: genericInputError,
      };
    }

    return {
      isValid: false,
      normalizedNumber: '',
      parsedPhone: null,
      errorKey: PHONE_VALIDATION_ERRORS.INVALID_PHONE,
    };
  }

  const brazilianValidation = validateBrazilianPhone(parsedPhone);
  if (!brazilianValidation.isValid) {
    return {
      isValid: false,
      normalizedNumber: '',
      parsedPhone,
      errorKey: brazilianValidation.errorKey,
    };
  }

  return {
    isValid: true,
    normalizedNumber: parsedPhone.number,
    parsedPhone,
    errorKey: '',
  };
};

export const normalizePhoneForInput = (value, activeDialCode = '') =>
  validatePhoneForInput(value, activeDialCode).normalizedNumber || '';
